import { FunctionComponent, useEffect, useState, useRef, ReactNode } from 'react';
import styles from './IframeLayout.module.scss';
import BackButton from '../../Input/BackButton/BackButton';
import SeparatorCurvetop from './separator/SeparatorCurveTop';
import { Color } from '../../DataDisplay/Icon/Icon';
import { getContrastColor, getCSSVariable } from '../../../utils/utils';

export type IframeLayoutProps = {
  separator: React.JSX.Element;
  image: React.JSX.Element;
  hasHeader?: boolean;
  hasBackButton: boolean;
  topRight?: React.JSX.Element;
  topRightScrollable?: boolean;
  paylinkContent?: React.JSX.Element;
  content: React.JSX.Element;
  pageCount?: number;
  pageTitle?: ReactNode;
  onBack?: () => void;
  color?: 'primary' | 'secondary' | 'outlined' | 'none' | 'none-light';
  primaryColor?: string;
  backButtonIconColor?: Color;
};

const IframeLayout: FunctionComponent<IframeLayoutProps> = ({
  separator,
  image,
  hasHeader = true,
  hasBackButton,
  paylinkContent,
  content,
  pageCount,
  pageTitle,
  topRight,
  topRightScrollable = false,
  color,
  primaryColor,
  onBack,
  backButtonIconColor,
}) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const laptopBreakpoint = getCSSVariable('--laptop');
  const mobileBreakpoint = getCSSVariable('--mobile');
  const isLaptop = matchMedia(`only screen and (max-width: ${laptopBreakpoint})`).matches;
  const isMobile = matchMedia(`only screen and (max-width: ${mobileBreakpoint})`).matches;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(scrollContainerRef.current?.scrollTop !== 0);
    };

    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      {hasHeader && (
        <div className={[styles.backBtn, styles[getShadowClass()], styles[getMobileClass()]].join(' ')}>
          <div className={styles.backBtnWrapper}>
            <div className={styles.mobileTop}>
              <SeparatorCurvetop fill={primaryColor || '#FFD147'} />
            </div>

            <div className={styles.backBtnContainer}>
              {hasBackButton && (
                <div>
                  <BackButton
                    backButtonEnabled
                    size="lg"
                    color={isLaptop ? 'primary' : color}
                    pageCount={pageCount}
                    variant="arrowIcon"
                    iconColor={
                      isLaptop
                        ? backButtonIconColor === 'primary-light'
                          ? backButtonIconColor
                          : 'primary-dark'
                        : getContrastColor(primaryColor || '') === '#2D2D2D'
                          ? 'primary-dark'
                          : 'primary-light'
                    }
                    onClick={onBack}
                  />
                </div>
              )}
              <div className={styles.pageTitle}>{pageTitle}</div>
              <div className={styles.topRightMobile}>{topRight}</div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.leftContainer}>
        {paylinkContent && paylinkContent}
        <div className={styles.noise} />
        <div className={styles.svgContainer}>
          <div className={styles.svg}>{separator}</div>
        </div>
        {image}
      </div>
      <div className={styles.rightContainer} ref={scrollContainerRef}>
        {topRight && topRightScrollable && <div className={styles.topRightScrollable}>{topRight}</div>}
        <div className={styles.formContainer}>{content}</div>
      </div>
      {topRight && !topRightScrollable && <div className={styles.topRight}>{topRight}</div>}
    </div>
  );

  function getShadowClass() {
    return isScrolling ? 'shadow' : '';
  }

  function getMobileClass() {
    return isMobile ? 'mobile' : '';
  }
};

export default IframeLayout;
